import type ExcelJS from 'exceljs';
import type { AlertType, EngagementAssessment, RiskTier } from '../types';
import { accountNameForDisplay } from './engagement-health';

const RISK_TIER_LABELS: Record<RiskTier, string> = {
  not_scored: 'Not scored',
  healthy: 'Healthy',
  watch: 'Watch',
  at_risk: 'At risk',
  critical: 'Critical',
};

const SIGNAL_LABELS: Record<AlertType, string> = {
  stalled_start: 'Stalled start',
  near_completion: 'Near completion',
  stale_activity: 'SOW ending / overdue',
  stale_activity_30d: 'No session 30d',
};

export async function buildRiskRankingWorkbook(
  engagements: EngagementAssessment[],
  now = new Date(),
): Promise<ExcelJS.Workbook> {
  const { default: Excel } = await import('exceljs');
  const workbook = new Excel.Workbook();
  workbook.creator = 'Ariel Engagement Health';
  workbook.created = now;

  const sheet = workbook.addWorksheet('Risk Ranking', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  sheet.columns = [
    { header: 'Risk score', key: 'riskScore', width: 12 },
    { header: 'Risk tier', key: 'riskTier', width: 14 },
    { header: 'Signals', key: 'signals', width: 40 },
    { header: 'Reporting account', key: 'reportingAccount', width: 26 },
    { header: 'Salesforce account', key: 'salesforceAccount', width: 28 },
    { header: 'Engagement', key: 'engagement', width: 36 },
    { header: 'Salesforce opportunity ID', key: 'opportunityId', width: 24 },
    { header: 'Coachee', key: 'coachee', width: 24 },
    { header: 'Projects', key: 'projects', width: 22 },
    { header: 'Owner', key: 'owner', width: 22 },
    { header: 'Owner email', key: 'ownerEmail', width: 30 },
    { header: 'CSM', key: 'csm', width: 22 },
    { header: 'CSM email', key: 'csmEmail', width: 30 },
    { header: 'Status', key: 'status', width: 16 },
    { header: 'Affiliates', key: 'affiliates', width: 30 },
    { header: 'Contracted hours', key: 'contractedHours', width: 18 },
    { header: 'Logged hours', key: 'loggedHours', width: 16 },
    { header: 'Remaining hours', key: 'remainingHours', width: 17 },
    { header: 'Utilization', key: 'utilization', width: 14 },
    { header: 'SOW start', key: 'sowStart', width: 14 },
    { header: 'SOW end', key: 'sowEnd', width: 14 },
    { header: 'Last session', key: 'lastSession', width: 14 },
    { header: 'Days since start', key: 'daysSinceStart', width: 17 },
    { header: 'Days since last session', key: 'daysSinceLastSession', width: 23 },
    { header: 'Days until end', key: 'daysUntilEnd', width: 16 },
  ];

  for (const engagement of engagements) {
    sheet.addRow({
      riskScore: engagement.riskScore,
      riskTier: RISK_TIER_LABELS[engagement.riskTier],
      signals: engagement.riskSignals
        .map((signal) => `${SIGNAL_LABELS[signal.type]} (+${signal.points})`)
        .join(', '),
      reportingAccount: accountNameForDisplay(engagement),
      salesforceAccount: engagement.accountName,
      engagement: engagement.opportunityName,
      opportunityId: engagement.opportunityId,
      coachee: engagement.coacheeName,
      projects: engagement.projectNumbers.join(', '),
      owner: engagement.ownerName,
      ownerEmail: engagement.ownerEmail,
      csm: engagement.csmName,
      csmEmail: engagement.csmEmail,
      status: engagement.engagementStatus?.trim() || null,
      affiliates: engagement.deliveringAffiliates
        .map((affiliate) => affiliate.name)
        .filter((name): name is string => !!name)
        .join(', '),
      contractedHours: engagement.contractedHours,
      loggedHours: engagement.loggedHours,
      remainingHours: engagement.contractedHours == null
        ? null
        : engagement.contractedHours - engagement.loggedHours,
      utilization: engagement.pctComplete,
      sowStart: excelDate(engagement.sowStartDate),
      sowEnd: excelDate(engagement.sowEndDate),
      lastSession: excelDate(engagement.lastSessionDate),
      daysSinceStart: engagement.daysSinceStart,
      daysSinceLastSession: engagement.daysSinceLastSession,
      daysUntilEnd: engagement.daysUntilEnd,
    });
  }

  const header = sheet.getRow(1);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF071D49' } };
  header.alignment = { vertical: 'middle' };
  header.height = 24;
  sheet.autoFilter = { from: 'A1', to: 'Y1' };
  ['T', 'U', 'V'].forEach((column) => { sheet.getColumn(column).numFmt = 'yyyy-mm-dd'; });
  sheet.getColumn('S').numFmt = '0%';

  return workbook;
}

export async function downloadRiskRankingWorkbook(
  engagements: EngagementAssessment[],
  now = new Date(),
): Promise<void> {
  const workbook = await buildRiskRankingWorkbook(engagements, now);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([new Uint8Array(buffer)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `Engagement-Health-Risk-Ranking-${now.toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function excelDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}
